// src/components/SponsorLogoFeld.jsx
//
// Sponsorlogo einer Station: Vorschau, „Logo wählen …“, „Entfernen“.
//
// Das Logo wird NICHT sofort hochgeladen, sondern wie die uebrigen Felder erst
// mit „Speichern“ – sonst haette „Abbrechen“ im Dialog keine Wirkung auf das
// Logo, und beim Anlegen gibt es die Station zum Zeitpunkt der Auswahl noch gar
// nicht. Der Hook merkt sich deshalb nur die Aenderung (`undefined` = keine,
// `null` = entfernen, String = neues Bild) und `logoSpeichern()` schickt sie
// nach dem Speichern der Station ab.
//
// Gespeichert wird getrennt von der Station (PUT/DELETE /stations/:nr/logo),
// siehe functions/services/sponsorLogos.js.

import { useEffect, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { logoVorbereiten } from "../utils/logoVorbereiten";
import Spinner from "./ui/Spinner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * @param {number|string|null} stationNumber  bestehende Station (null beim Anlegen)
 * @param {boolean} hatLogo  Kennzeichen `sponsor_logo` der Station
 * @param {boolean} aktiv    Dialog offen und Nutzer ist Admin
 */
export function useSponsorLogo(stationNumber, hatLogo, aktiv) {
  const [vorschau, setVorschau] = useState(null);
  const [aenderung, setAenderung] = useState(undefined);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    setVorschau(null);
    setAenderung(undefined);
    setFehler("");
    if (!aktiv || !stationNumber || !hatLogo) return;

    let abgebrochen = false;
    setLaedt(true);
    fetchWithAuth(`${BACKEND_URL}/stations/${stationNumber}/logo`, null)
      .then((logo) => {
        if (abgebrochen) return;
        const typ = logo.format === "png" ? "png" : "jpeg";
        setVorschau(`data:image/${typ};base64,${logo.data}`);
      })
      .catch((err) => {
        // 404: Kennzeichen steht noch, Logo fehlt – dann eben keins anzeigen.
        if (!abgebrochen && err.status !== 404) setFehler("Logo konnte nicht geladen werden.");
      })
      .finally(() => !abgebrochen && setLaedt(false));
    return () => {
      abgebrochen = true;
    };
  }, [stationNumber, hatLogo, aktiv]);

  const waehle = async (datei) => {
    setFehler("");
    setLaedt(true);
    try {
      const { dataUrl } = await logoVorbereiten(datei);
      setVorschau(dataUrl);
      setAenderung(dataUrl);
    } catch (err) {
      setFehler(err.message || "Logo konnte nicht verarbeitet werden.");
    } finally {
      setLaedt(false);
    }
  };

  const entferne = () => {
    setFehler("");
    setVorschau(null);
    // Gab es vorher keins, ist "entfernen" nur das Verwerfen der Auswahl.
    setAenderung(hatLogo ? null : undefined);
  };

  return { vorschau, aenderung, laedt, fehler, waehle, entferne };
}

/** Offene Logo-Aenderung fuer `stationNumber` abschicken (nach dem Speichern). */
export async function logoSpeichern(stationNumber, aenderung) {
  if (aenderung === undefined) return;
  const url = `${BACKEND_URL}/stations/${stationNumber}/logo`;
  if (aenderung === null) {
    await fetchWithAuth(url, null, { method: "DELETE" });
  } else {
    await fetchWithAuth(url, null, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: aenderung }),
    });
  }
}

export default function SponsorLogoFeld({ logo, disabled, bearbeitbar = true }) {
  const dateiRef = useRef(null);
  const { vorschau, laedt, fehler, waehle, entferne } = logo;

  // Ansichtsmodus ohne Logo: das Feld waere nur Rauschen.
  if (!bearbeitbar && !vorschau && !laedt) return null;

  return (
    <div className="space-y-1">
      <label className="text-sm text-gray-400">Sponsorlogo</label>
      <div className="flex items-center gap-3">
        {/* Weisser Grund wie auf dem Poster und dem Zettel – so sieht man, ob
            ein dunkles Logo mit transparentem Hintergrund lesbar bleibt. */}
        <div className="h-20 w-48 shrink-0 rounded-lg bg-white border border-secondary-700 flex items-center justify-center overflow-hidden">
          {laedt ? (
            <Spinner size={5} />
          ) : vorschau ? (
            <img src={vorschau} alt="Sponsorlogo" className="max-h-16 max-w-[11rem] object-contain" />
          ) : (
            <span className="text-xs text-gray-500">kein Logo</span>
          )}
        </div>

        {bearbeitbar && (
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => dateiRef.current?.click()}
              disabled={disabled || laedt}
              className="inline-flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 disabled:opacity-50"
            >
              <ImagePlus className="w-4 h-4" />
              {vorschau ? "Anderes Logo wählen …" : "Logo wählen …"}
            </button>
            {vorschau && (
              <button
                type="button"
                onClick={entferne}
                disabled={disabled || laedt}
                className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-red-400 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                Entfernen
              </button>
            )}
            <input
              ref={dateiRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const datei = e.target.files?.[0];
                e.target.value = ""; // dieselbe Datei erneut waehlbar
                if (datei) waehle(datei);
              }}
            />
          </div>
        )}
      </div>
      {bearbeitbar && (
        <p className="text-xs text-gray-500">
          Erscheint auf dem Stationsposter und – wenn Platz ist – auf der Rückseite der
          Team-Zettel. Am besten PNG mit transparentem oder weißem Hintergrund.
        </p>
      )}
      {fehler && <p className="text-xs text-red-400">{fehler}</p>}
    </div>
  );
}
